import { useQuery } from '@tanstack/react-query'
import { useCallback, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { api, unwrap } from '../api/client'
import type { ModuleNode } from '../api/types'
import { ActivityChart } from '../engagement/ActivityChart'
import { BoardCard } from '../engagement/BoardCard'
import { FeedCard } from '../engagement/FeedCard'
import { ForecastCard } from '../features/dailylog/Forecast'
import { RecentPhotosCard } from '../photos/RecentPhotosCard'
import { t, tCount, tOr, useWording } from '../i18n'
import { Chip, Notice, Pending, formatDate } from '../ui'
import { useCourt, type CourtQueue, type CourtRow } from './court'
import { Customize } from './Customize'
import { COLUMN, useDashboardLayout, type CardKey } from './dashboard'
import { MilestonesCard } from './MilestonesCard'
import { menuEntries } from './nav'
import { useScreenRoute } from './screen'
import { canOpen, useSession } from './session'

/**
 * The first screen after signing in: what is waiting on you, then what is
 * happening around you.
 *
 * "In your court" comes from the modules that publish a court endpoint rather
 * than from a query this screen invents, because who holds a record is a
 * lifecycle question each module answers for itself — and a browser that
 * recomputes it will disagree with the notification that told somebody to look.
 *
 * It now reads every one of them. Five registers publish that endpoint and
 * this screen asked one, so the card said "nothing is waiting on you" to
 * somebody holding a submittal, a change event, a letter and three meeting
 * actions. Which registers exist is read from the application's own document
 * rather than listed here; see `court.ts`.
 */
export function Overview() {
  const placement = useScreenRoute('/engage/feed', '')
  const projectId = placement.project.id

  const { me } = useSession()
  const modules = me.data?.modules ?? []
  const court = useCourt(modules, projectId)
  const where = courtDestinations(modules, projectId)

  const deficiencies = useQuery({
    queryKey: ['deficiency', 'mine', projectId],
    retry: false,
    queryFn: async () =>
      unwrap(
        await api.GET('/v1/deficiencies/', {
          params: { query: { project_id: projectId, limit: 5 } },
        }),
      ),
  })

  const [layout, setLayout] = useDashboardLayout(me.data?.person.id)
  const [customizing, setCustomizing] = useState(false)
  const closeCustomize = useCallback(() => setCustomizing(false), [])

  // A card for a module this person cannot open on this job is not offered:
  // it could only ever say it was refused.
  const offered: CardKey[] = layout.order.filter(
    (card) =>
      (card !== 'milestones' || canOpen(placement, 'schedule')) &&
      (card !== 'forecast' || canOpen(placement, 'dailylog')) &&
      (card !== 'photos' || canOpen(placement, 'photos')),
  )
  const shown = offered.filter((card) => !layout.hidden.includes(card))

  const cards: Record<CardKey, ReactNode> = {
    court: (
      <CourtCard
        court={court}
        punch={{
          isPending: deficiencies.isPending,
          isError: deficiencies.isError,
          rows: deficiencies.data ?? [],
        }}
        where={where}
        projectId={projectId}
      />
    ),
    activity: <FeedCard projectId={projectId} />,
    chart: <ActivityChart projectId={projectId} modules={modules} />,
    forecast: <ForecastCard projectId={projectId} />,
    board: <BoardCard projectId={projectId} modules={modules} />,
    milestones: <MilestonesCard projectId={projectId} />,
    photos: <RecentPhotosCard projectId={projectId} />,
  }

  return (
    <>
      <div className="row">
        <h2 className="page" style={{ flex: 1 }}>
          {t('home.title')}
        </h2>
        <Link className="text-link" to={`/p/${projectId}/my-email`}>
          {t('mail.mine.open')}
        </Link>
        <button type="button" className="text-link" onClick={() => setCustomizing(true)}>
          {t('home.customize.open')}
        </button>
      </div>
      <p className="page-sub">
        {t('home.subtitle', {
          project: `${placement.project.number} ${placement.project.name}`,
          address: placement.address.label,
        })}{' '}
        · <span className="chip">{tOr(
          `nav.delivery_mode.${placement.project.delivery_mode}`,
          placement.project.delivery_mode,
        )}</span>
      </p>

      <div className="columns">
        {(['main', 'side'] as const).map((column) => (
          <div key={column}>
            {shown
              .filter((card) => COLUMN[card] === column)
              .map((card) => (
                <div key={card}>{cards[card]}</div>
              ))}
          </div>
        ))}
      </div>
      {customizing ? (
        <Customize
          layout={layout}
          offered={offered}
          onChange={setLayout}
          onClose={closeCustomize}
        />
      ) : null}
    </>
  )
}

/** Where a register's queue entries go, keyed by its route prefix. */
export type CourtDestination = {
  label: string
  /** The register's own screen, as the menu reaches it. */
  path: string
  /** Whether `<path>/<id>` opens the record a queue entry names. */
  records?: boolean
}

/**
 * Registers whose queue entries open the record itself.
 *
 * Two conditions, and both have to hold: the register has a designed screen
 * that opens one record at `<path>/<id>`, and the identifier its queue carries
 * is that record's own. The RFI and submittal queues carry `rfi_id` and
 * `submittal_id`, and sending an entry for one draft to the whole log left the
 * reader hunting for the line they had just pressed. A meetings entry is an
 * action item and the board opens occurrences, and the generated screens open
 * no record at all, so those still go to the register.
 */
const OPENS_RECORDS = new Set(['CM-RFI', 'CM-SUB'])

/**
 * Where a queue entry came from and where clicking it goes. Built from the
 * same entries the menu uses, so a row opens the screen the reader would
 * have reached from the menu — the designed one when the module has it, the
 * generated one when it does not — and never a path invented here.
 */
export function courtDestinations(
  modules: ModuleNode[],
  projectId: string,
): Map<string, CourtDestination> {
  return new Map(
    menuEntries(modules, projectId).map((entry) => {
      const module = modules.find((node) => node.code === entry.code)
      return [
        module?.route_prefix ?? entry.code,
        {
          label: entry.label,
          path: entry.path,
          records: entry.built && OPENS_RECORDS.has(entry.code),
        },
      ]
    }),
  )
}

/** A punch-list row as the card draws it. The register answers with more. */
type PunchRow = {
  id: string
  display_number: string
  title: string
  state: string
}

/** What feeds the card besides the five `/court` registers. */
export type PunchSource = {
  isPending: boolean
  isError: boolean
  rows: PunchRow[]
}

/**
 * What the card is entitled to say, given every source that feeds it.
 *
 * **Six sources answer this card and only five of them are `/court`
 * registers**: the punch list is fetched beside them. Counting the five was
 * the fault the owner reported. It showed twice. A person whose punch list
 * alone was denied saw no warning at all and "Nothing is waiting on you"
 * underneath it, because the count the warning is gated on never left zero.
 * A person denied everything saw the count say five while six had failed.
 *
 * The distinction the card has to carry is between *we looked and there is
 * nothing* and *we could not look*. Only the first of those is an empty work
 * queue, and only the first may be said. On a construction project the cost of
 * a wrongly empty work queue is a thing that does not get done.
 *
 * A source still loading is not a source that failed, and it is not an empty
 * queue either: until every one of them has answered the card says neither.
 */
export function coverage(
  court: CourtQueue,
  punch: PunchSource,
): { failed: number; pending: boolean; empty: boolean } {
  const failed = court.failed + (punch.isError ? 1 : 0)
  const pending = court.pending || punch.isPending
  return {
    failed,
    pending,
    // Every source answered, and every one of them answered with nothing.
    empty: !pending && failed === 0 && court.rows.length === 0 && punch.rows.length === 0,
  }
}

/**
 * "In your court": what is waiting on the person reading it.
 *
 * Separated from the screen around it so the rule above can be rendered in a
 * test rather than asserted about in a comment. It takes its sources as
 * values: the screen owns the fetching, this owns what may be said about it.
 *
 * Whatever did answer is drawn whether or not the rest did, with the warning
 * above it saying the list is incomplete — retained results shown as partial,
 * which is a different thing from a queue that is empty.
 */
export function CourtCard({
  court,
  punch,
  where,
  projectId,
}: {
  court: CourtQueue
  punch: PunchSource
  where: Map<string, CourtDestination>
  projectId: string
}) {
  // A row can be drawn before the instance's wording for it has arrived; this
  // draws the card again when it does, so the key is replaced by its sentence.
  useWording()
  const cover = coverage(court, punch)
  return (
    <section className="card" aria-labelledby="court-heading">
      <h3 id="court-heading">{t('home.my_court')}</h3>
      {cover.pending ? <Pending /> : null}
      {cover.failed > 0 ? (
        <Notice tone="warn">{tCount('home.my_court.unreachable', cover.failed)}</Notice>
      ) : null}
      {cover.empty ? <p className="muted">{t('home.my_court.empty')}</p> : null}
      <ul className="feed">
        {court.rows.map((row) => (
          <CourtEntry key={`${row.prefix}:${row.id}`} row={row} where={where} />
        ))}
        {punch.rows.map((row) => (
          <li key={row.id}>
            <Link to={`/p/${projectId}/deficiency/${row.id}`}>
              <span className="mono">{row.display_number}</span> {row.title}
            </Link>{' '}
            <Chip value={row.state} prefix="deficiency.state" />
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * One queue entry, wearing the name of the register it came from.
 *
 * The register is shown rather than inferred from the number: `COR-VM-00004`
 * and `SUB-VM-00004` are the same shape to somebody scanning a list, and the
 * whole value of one queue is not having to work out which log a line belongs
 * to. What the person owes is the instance's own wording for `action_key` —
 * the message catalogue is fetched at sign-in — and never the key itself.
 */
function CourtEntry({
  row,
  where,
}: {
  row: CourtRow
  where: Map<string, CourtDestination>
}) {
  const home = where.get(row.prefix)
  const line = (
    <>
      <span className="mono">{row.number}</span> {row.label}
    </>
  )
  const to = home?.records ? `${home.path}/${encodeURIComponent(row.id)}` : home?.path
  return (
    <li>
      {to ? <Link to={to}>{line}</Link> : line}
      <span className="when">
        {home ? <span className="chip">{home.label}</span> : null}{' '}
        {tOr(row.actionKey, row.actionKey)}
        {row.dueOn ? ` · ${formatDate(row.dueOn)}` : ''}
        {row.overdue ? ' ⚠' : ''}
      </span>
    </li>
  )
}
